import Vue from 'vue'
import Router from 'vue-router'
import Api from '@/providers/Api'
import _ from "lodash";
import {ScreenOrientation} from "@capacitor/screen-orientation";
import {Capacitor} from "@capacitor/core";
import { App, URLOpenListenerEvent } from '@capacitor/app';

Vue.use(Router)

// Prevent NavigationDuplicated error
const originalPush = Router.prototype.push
Router.prototype.push = function push(location) {
    return originalPush.call(this, location).catch(err => {
        if (err.name !== 'NavigationDuplicated') throw err
    })
}

// Listen for app deeplinks
App.addListener('appUrlOpen', function (urlEvent) {
    const slug = urlEvent.url.split('.com').pop();
    if (slug) {
        router.push({
            path: slug,
        });
    }
});

const router = new Router(
    {
        mode: 'history',
        routes: [
            // ----------- Public Pages
            {
                path: '/',
                component: () => import('@/pages/Home'),
            },
            {
                path: '/signup',
                component: () => import('@/pages/UserSignup'),
                meta: {
                    security: () => Capacitor.isNativePlatform()
                }
            },
            {
                path: '/tutorial',
                component: () => import('@/pages/Tutorial'),
            },
            {
                path: '/about',
                component: () => import('@/pages/About'),
            },
            {
                path: '/contact',
                component: () => import('@/pages/Contact'),
            },
            {
                path: '/terms',
                component: () => import('@/pages/Terms'),
            },
            {
                path: '/privacy',
                component: () => import('@/pages/Privacy'),
            },
            {
                path: '/login',
                component: () => import('@/pages/UserLogin')
            },
            {
                path: '/resetPassword',
                component: () => import('@/pages/UserResetPassword.vue'),
            },

            // ----------- Authenticated User Pages
            {
                path: '/locations',
                component: () => import('@/pages/Locations'),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/locations/create',
                component: () => import('@/pages/LocationsCreate'),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/locations/:locId/view',
                component: () => import('@/pages/LocationsView'),
                props: (route) => ({
                    locId: route.params.locId,
                }),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/locations/:locId/edit',
                component: () => import('@/pages/LocationsEdit'),
                props: (route) => ({
                    locId: route.params.locId,
                }),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/locations/:locId/cards/create',
                component: () => import('@/pages/CardsCreate'),
                props: (route) => ({
                    initialLocId: route.params.locId,
                }),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/locations/:locId/cards/:cardId/edit',
                component: () => import('@/pages/CardsEdit'),
                props: (route) => ({
                    locId: route.params.locId,
                    cardId: route.params.cardId,
                }),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/locations/:locId/cards/:cardId/view',
                component: () => import('@/pages/CardsView'),
                props: (route) => ({
                    locId: route.params.locId,
                    cardId: route.params.cardId,
                }),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/scouts/create',
                component: () => import('@/pages/ScoutsEdit'),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/locations/:locId/cards/:cardId/scouts/create',
                component: () => import('@/pages/ScoutsEdit'),
                props: (route) => ({
                    locId: route.params.locId,
                    cardId: route.params.cardId,
                }),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/locations/:locId/cards/:cardId/scouts/:scoutId/edit',
                component: () => import('@/pages/ScoutsEdit'),
                props: (route) => ({
                    locId: route.params.locId,
                    cardId: route.params.cardId,
                    scoutId: route.params.scoutId,
                }),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/locations/:locId/cards/:cardId/scouts/:scoutId/view',
                component: () => import('@/pages/ScoutsView.vue'),
                props: (route) => ({
                    locId: route.params.locId,
                    cardId: route.params.cardId,
                    scoutId: route.params.scoutId,
                }),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/applications/create',
                component: () => import('@/pages/ApplicationsEdit'),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/locations/:locId/cards/:cardId/applications/:applicationId/view',
                component: () => import('@/pages/ApplicationsView.vue'),
                props: (route) => ({
                    locId: route.params.locId,
                    cardId: route.params.cardId,
                    applicationId: route.params.applicationId,
                    meta: {
                        security: () => Api.userLoggedIn()
                    }
                }),
            },
            {
                path: '/locations/:locId/cards/:cardId/applications/create',
                component: () => import('@/pages/ApplicationsEdit'),
                props: (route) => ({
                    locId: route.params.locId,
                    cardId: route.params.cardId,
                }),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/locations/:locId/cards/:cardId/applications/:applicationId/edit',
                component: () => import('@/pages/ApplicationsEdit'),
                props: (route) => ({
                    locId: route.params.locId,
                    cardId: route.params.cardId,
                    applicationId: route.params.applicationId,
                }),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/locations/:locId/cards/:cardId/chart',
                component: () => import('@/pages/CardsChart'),
                props: (route) => ({
                    locId: route.params.locId,
                    cardId: route.params.cardId,
                }),
                meta: {
                    allowRotate: true,
                    security: () => Api.userLoggedIn()
                }
            },

            // ----------- Authenticated User/Admin pages

            {
                path: '/beneficials',
                component: () => import('@/pages/Beneficials'),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/beneficials/view/:beneficialId',
                component: () => import('@/pages/Beneficials'),
                props: (route) => ({
                    beneficialId: route.params.beneficialId
                }),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/beneficials/:beneficialId/edit',
                component: () => import('@/pages/BeneficialsEdit'),
                props: (route) => ({
                    beneficialId: route.params.beneficialId,
                }),
                meta: {
                    security: () => Api.isAdmin()
                }
            },
            {
                path: '/beneficials/create',
                component: () => import('@/pages/BeneficialsCreate'),
                meta: {
                    security: () => Api.isAdmin()
                }
            },
            {
                path: '/pests',
                component: () => import('@/pages/Pests'),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/pests/view/:pestId',
                component: () => import('@/pages/Pests'),
                props: (route) => ({
                    pestId: route.params.pestId,
                    meta: {
                        security: () => Api.userLoggedIn()
                    }
                }),
            },
            {
                path: '/pests/:pestTypeId/:pestId/edit',
                component: () => import('@/pages/PestsEdit'),
                props: (route) => ({
                    pestTypeId: route.params.pestTypeId,
                    pestId: route.params.pestId,
                }),
                meta: {
                    security: () => Api.isAdmin()
                }
            },
            {
                path: '/pests/create',
                component: () => import('@/pages/PestsCreate'),
                meta: {
                    security: () => Api.isAdmin()
                }
            },

            {
                path: '/symptoms',
                component: () => import('@/pages/Symptoms'),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/symptoms/view/:symptomId',
                component: () => import('@/pages/Symptoms'),
                props: (route) => ({
                    symptomId: route.params.symptomId,
                }),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/symptoms/:symptomId/edit',
                component: () => import('@/pages/SymptomsEdit'),
                props: (route) => ({
                    symptomId: route.params.symptomId,
                }),
                meta: {
                    security: () => Api.isAdmin()
                }
            },
            {
                path: '/symptoms/create',
                component: () => import('@/pages/SymptomsCreate'),
                meta: {
                    security: () => Api.isAdmin()
                }
            },

            {
                path: '/diseases',
                component: () => import('@/pages/Diseases'),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/diseases/view/:diseaseId',
                component: () => import('@/pages/Diseases'),
                props: (route) => ({
                    diseaseId: route.params.diseaseId,
                }),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/diseases/:diseaseId/edit',
                component: () => import('@/pages/DiseasesEdit'),
                props: (route) => ({
                    diseaseId: route.params.diseaseId,
                }),
                meta: {
                    security: () => Api.isAdmin()
                }
            },
            {
                path: '/diseases/create',
                component: () => import('@/pages/DiseasesCreate'),
                meta: {
                    security: () => Api.isAdmin()
                }
            },

            {
                path: '/tasks',
                component: () => import('@/pages/Tasks'),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/tasks/:taskId/edit',
                component: () => import('@/pages/TasksEdit'),
                props: (route) => ({
                    taskId: route.params.taskId,
                }),
                meta: {
                    security: () => Api.isAdmin()
                }
            },
            {
                path: '/tasks/create',
                component: () => import('@/pages/TasksCreate'),
                meta: {
                    security: () => Api.isAdmin()
                }
            },

            {
                path: '/pesticides',
                component: () => import('@/pages/Pesticides'),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/pesticides/view/:pesticideId',
                component: () => import('@/pages/Pesticides'),
                props: (route) => ({
                    pesticideId: route.params.pesticideId
                }),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/pesticides/:pesticideId/edit',
                component: () => import('@/pages/PesticidesEdit'),
                props: (route) => ({
                    pesticideId: route.params.pesticideId,
                }),
                meta: {
                    security: () => Api.isAdmin()
                }
            },
            {
                path: '/pesticides/create',
                component: () => import('@/pages/PesticidesCreate'),
                meta: {
                    security: () => Api.isAdmin()
                }
            },

            {
                path: '/interactions',
                component: () => import('@/pages/Interactions'),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },
            {
                path: '/interactions/beneficials/:beneficialId',
                component: () => import('@/pages/Interactions'),
                props: (route) => ({
                    beneficialId: route.params.beneficialId,
                }),
                meta: {
                    security: () => Api.userLoggedIn()
                }
            },

            // Admin Pages
            {
                path: '/users',
                component: () => import('@/pages/UserManager'),
                meta: {
                    security: () => Api.isAdmin()
                }
            },
            {
                path: '/users/create',
                component: () => import('@/pages/UserCreate'),
                meta: {
                    security: () => Api.isAdmin()
                }
            },
            {
                path: '/user/:userId/edit',
                component: () => import('@/pages/UserEdit'),
                props: (route) => ({
                    userId: route.params.userId,
                }),
                meta: {
                  security: () => Api.isAdmin()
                }
            },
            {
                path: '/profile',
                component: () => import('@/pages/UserProfile'),
                meta: {
                    security: () => Api.isVerified()
                }
            },
            {
                path: '/database',
                component: () => import('@/pages/DatabaseManager'),
                meta: {
                    security: () => Api.isAdmin()
                }
            },

            // default: page not found
            {
                path: '*',
                component: () => import('@/pages/NotFound'),
            },
        ],
    }
)


const pageTitles = {
    '/': 'Home',
    '/signup': 'Sign Up',
    '/tutorial': 'Tutorial',
    '/about': 'About',
    '/contact': 'Contact',
    '/terms': 'Terms of Use',
    '/privacy': 'Privacy Policy',
    '/login': 'Log In',
    '/resetPassword': 'Reset Password',
    '/locations': 'Locations',
    '/locations/create': 'New Location',
    '/locations/:locId/view': 'View Location',
    '/locations/:locId/edit': 'Edit Location',
    '/locations/:locId/cards/create': 'New Card',
    '/locations/:locId/cards/:cardId/edit': 'Edit Card',
    '/locations/:locId/cards/:cardId/view': 'View Card',
    '/scouts/create': 'New Scout',
    '/locations/:locId/cards/:cardId/scouts/create': 'New Scout',
    '/locations/:locId/cards/:cardId/scouts/:scoutId/edit': 'Edit Scout',
    '/locations/:locId/cards/:cardId/scouts/:scoutId/view': 'View Scout',
    '/applications/create': 'New Application',
    '/locations/:locId/cards/:cardId/applications/:applicationId/view': 'View Application',
    '/locations/:locId/cards/:cardId/applications/create': 'New Application',
    '/locations/:locId/cards/:cardId/applications/:applicationId/edit': 'Edit Application',
    '/locations/:locId/cards/:cardId/chart': 'Scout Chart',
    '/beneficials': 'Beneficials',
    '/beneficials/view/:beneficialId': 'Beneficials',
    '/beneficials/:beneficialId/edit': 'Edit Beneficial',
    '/beneficials/create': 'New Beneficial',
    '/pests': 'Pests',
    '/pests/view/:pestId': 'Pests',
    '/pests/:pestTypeId/:pestId/edit': 'Edit Pest',
    '/pests/create': 'New Pest',
    '/symptoms': 'Symptoms',
    '/symptoms/view/:symptomId': 'Symptoms',
    '/symptoms/:symptomId/edit': 'Edit Symptom',
    '/symptoms/create': 'New Symptom',
    '/diseases': 'Diseases',
    '/diseases/view/:diseaseId': 'Diseases',
    '/diseases/:diseaseId/edit': 'Edit Disease',
    '/diseases/create': 'New Disease',
    '/tasks': 'Tasks',
    '/tasks/:taskId/edit': 'Edit Task',
    '/tasks/create': 'New Task',
    '/pesticides': 'Pesticides',
    '/pesticides/view/:pesticideId': 'Pesticides',
    '/pesticides/:pesticideId/edit': 'Edit Pesticide',
    '/pesticides/create': 'New Pesticide',
    '/interactions': 'Pesticide Interactions',
    '/interactions/beneficials/:beneficialId': 'Pesticide Interactions',
    '/users': 'User Management',
    '/users/create': 'New User',
    '/user/:userId/edit': 'Edit User',
    '/profile': 'My Profile',
    '/database': 'Database Management',
    '*': 'Page Not Found',
}

router.afterEach((to) => {
    const routePattern = to.matched.length ? to.matched[0].path : null
    const title = pageTitles[routePattern]
    document.title = title ? `${title} | Greenhouse Scout` : 'Greenhouse Scout – Pocket IPM'
})


// navigation guard - check authorization
router.beforeEach(async (to, from, next) => {
    // check for security rules
    let allow = true
    if (_.isFunction(to.meta.security)) {
        // wait for api to initialize
        await Api.isInitialized
        // call security function defined by route
        allow = to.meta.security()
    }

    if (!allow) {
        router.app.$toasted.error('Unauthorized', { duration: 5000 })
        next(from)
    } else {
        next()
    }
    if (Capacitor.isNativePlatform()) {
        if (from.meta && from.meta.allowRotate) {
            await ScreenOrientation.lock({orientation: 'portrait'})
        }
        if (to.meta && to.meta.allowRotate) {
            await ScreenOrientation.unlock()
        }
    }
})


export default router


